import { Upload, Button, Form, message, Typography, Alert } from "antd";
import { UploadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { uploadMenu } from "../actions/merchant_actions";
import { useSelector } from "react-redux";
import ContentLayout from "../components/layout/ContentLayout";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const REQUIRED_COLUMNS = [
  "name",
  "currency_code",
  "minimum_payment_amount",
  "maximum_payment_amount",
];

const MerchantForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account_urn = useSelector((state) => state.account.urn);

  const beforeUpload = async (file) => {
    const isXlsx =
      file.name.endsWith(".xlsx") &&
      (file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "");

    const isLt8M = file.size / 1024 / 1024 < 8;

    if (!isXlsx) {
      message.error(t("only_xlsx_error"));
      return Upload.LIST_IGNORE;
    }

    if (!isLt8M) {
      message.error(t("file_size_error"));
      return Upload.LIST_IGNORE;
    }

    const isValid = await validateFileColumns(file);
    if (!isValid) {
      message.error(t("invalid_file_error"));
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const validateFileColumns = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        const headers = rows[0]?.map((h) => String(h).trim().toLowerCase());

        const isValid = REQUIRED_COLUMNS.every((col) => headers.includes(col));

        resolve(isValid);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const customUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await uploadMenu({ account_urn, data: formData });
      message.success(t("upload_success", { fileName: file.name }));
      onSuccess("ok");
      navigate("/merchant");
    } catch (err) {
      console.error(err);
      message.error(t("upload_failed"));
      onError(err);
    }
  };

  return (
    <ContentLayout
      header={
        <div className="header">
          <Title level={3}>{t("upload_order_file")}</Title>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/merchant")}
          >
            {t("return")}
          </Button>
        </div>
      }
    >
      <div className="order-form-wrapper">
        <Alert
          type="info"
          showIcon
          message={t("instructions")}
          description={
            <ul style={{ marginLeft: "16px" }}>
              <li>{t("only_xlsx_allowed")}</li>
              <li>{t("max_size")}</li>
              <li>
                {t("required_columns")}{" "}
                <code>
                  name, currency_code, minimum_payment_amount,
                  maximum_payment_amount
                </code>
              </li>
              <li>{t("headers_first_row")}</li>
            </ul>
          }
          style={{ marginBottom: 20 }}
        />

        <Form layout="vertical">
          <Form.Item label={t("select_xlsx_file")}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Upload
                customRequest={customUpload}
                beforeUpload={beforeUpload}
                showUploadList={false}
              >
                <Button type="primary" icon={<UploadOutlined />}>
                  {t("click_to_upload")}
                </Button>
              </Upload>

              <Button
                type="default"
                href="https://com-parktechnology-dev-eu-assets.s3.eu-west-1.amazonaws.com/examples/Magic+Stamp+-+Order+Ahead+Menu+-+Magic+Stamp+Training.xlsx"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("download_example_sheet")}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </ContentLayout>
  );
};

export default MerchantForm;
